/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

import { useMemo } from 'react';
import { MRT_ColumnDef, MRT_RowSelectionState } from 'mantine-react-table';
import { useTranslation } from 'react-i18next';
import { ActionIcon, Group, Tooltip } from '@mantine/core';
import { IconFolderMinus, IconFolderPlus } from '@tabler/icons-react';

import { ResourceIndexView, ResourceType } from '@app/types/blackroom-api';
import CellResource from '@features/Resource/ResourceTable/cells/CellResource';

/* -------------------------------------------------------------------------- */
/*                               Implementation                               */
/* -------------------------------------------------------------------------- */

interface UseColumnsOptions {
  globalFilter: string;
  onClickResourceName: (resourceId: string) => void;
  onToggleFolderContentSelection: (resourceId: string) => void;
  getFolderContentIds: (resourceId: string) => string[];
  rowSelection: MRT_RowSelectionState;
}

export default function useColumns({
  globalFilter,
  onClickResourceName,
  onToggleFolderContentSelection,
  getFolderContentIds,
  rowSelection,
}: UseColumnsOptions) {
  const { t } = useTranslation(['Resource', 'AIAssistant']);

  const columnDefs = useMemo<MRT_ColumnDef<ResourceIndexView>[]>(
    () =>
      ['name', 'index'].map((col) => {
        const sharedColDef: MRT_ColumnDef<ResourceIndexView> = {
          accessorKey: col,
          enableSorting: false,
          enableColumnActions: false,
          grow: false,
          header: t(`ResourceTable.columns.${col}.label`),
        };
        switch (col) {
          case 'name':
            return {
              ...sharedColDef,
              filterVariant: 'text',
              minSize: 300,
              grow: true,
              enableGlobalFilter: true,
              Cell: (props) => {
                const resource = props.row.original;
                const folderContentIds = getFolderContentIds(resource.id);
                const hasFolderContent =
                  resource.type === ResourceType.Folder &&
                  folderContentIds.length > 0;
                const isFolderContentSelected =
                  hasFolderContent &&
                  folderContentIds.every((id) => rowSelection[id]);
                const folderContentActionKey = isFolderContentSelected
                  ? 'AIAssistant:SectionRename.folderContentAction.remove'
                  : 'AIAssistant:SectionRename.folderContentAction.add';
                const folderContentActionLabel = t(folderContentActionKey);
                const cellContent = (
                  <CellResource<ResourceIndexView>
                    {...props}
                    globalFilter={globalFilter}
                  />
                );

                return (
                  <Group gap="xs" wrap="nowrap" w="100%">
                    <div
                      onClick={(e) => {
                        e.stopPropagation();
                        onClickResourceName(resource.id);
                      }}
                      style={{
                        flex: 1,
                        minWidth: 0,
                        position: 'relative',
                        cursor: 'pointer',
                      }}
                    >
                      {cellContent}
                    </div>
                    {hasFolderContent && (
                      <Tooltip label={folderContentActionLabel} openDelay={400}>
                        <ActionIcon
                          size="sm"
                          variant={isFolderContentSelected ? 'light' : 'subtle'}
                          color={
                            isFolderContentSelected
                              ? 'blackroomRed'
                              : 'blackroomBlue'
                          }
                          aria-label={folderContentActionLabel}
                          style={{ flexShrink: 0 }}
                          onClick={(event) => {
                            event.stopPropagation();
                            onToggleFolderContentSelection(resource.id);
                          }}
                        >
                          {isFolderContentSelected ? (
                            <IconFolderMinus size={16} />
                          ) : (
                            <IconFolderPlus size={16} />
                          )}
                        </ActionIcon>
                      </Tooltip>
                    )}
                  </Group>
                );
              },
            };

          case 'index':
            return {
              ...sharedColDef,
              size: 110,
            };

          default:
            break;
        }
        return sharedColDef;
      }),
    [
      onClickResourceName,
      onToggleFolderContentSelection,
      getFolderContentIds,
      rowSelection,
      globalFilter,
      t,
    ],
  );

  return columnDefs;
}
